import type { DeliverySchedule } from './maestroDeliveryClient';

export interface DeliverySchedulePreset {
  label: string;
  cron: string;
  group: 'Daily' | 'Weekly' | 'Monthly';
}

/** Preset schedules (UTC). Mirrors blox-dataflow-ui connection editor. */
export const DELIVERY_SCHEDULE_PRESETS: DeliverySchedulePreset[] = [
  { group: 'Daily', cron: '0 0 * * *', label: 'Every day at midnight UTC' },
  { group: 'Daily', cron: '0 3 * * *', label: 'Every day at 3 AM UTC' },
  { group: 'Daily', cron: '0 6 * * *', label: 'Every day at 6 AM UTC' },
  { group: 'Daily', cron: '0 9 * * *', label: 'Every day at 9 AM UTC' },
  { group: 'Weekly', cron: '0 3 * * 0', label: 'Every Sunday at 3 AM UTC' },
  { group: 'Weekly', cron: '0 3 * * 6', label: 'Every Saturday at 3 AM UTC' },
  { group: 'Weekly', cron: '0 3 * * 1', label: 'Every Monday at 3 AM UTC' },
  { group: 'Weekly', cron: '0 3 * * 1-5', label: 'Mon–Fri at 3 AM UTC' },
  { group: 'Weekly', cron: '0 9 * * 1-5', label: 'Mon–Fri at 9 AM UTC' },
  { group: 'Monthly', cron: '0 3 1 * *', label: '1st of each month at 3 AM UTC' },
];

export const DELIVERY_SCHEDULE_PRESET_CRONS = new Set(
  DELIVERY_SCHEDULE_PRESETS.map((row) => row.cron),
);

export const DEFAULT_DELIVERY_SCHEDULE_CRON = '0 3 * * 1';

const presetLabel = (cron: string): string | undefined =>
  DELIVERY_SCHEDULE_PRESETS.find((row) => row.cron === cron)?.label;

/** Map legacy weekly fields to a preset cron when possible. */
export function legacyWeeklyToCron(schedule?: DeliverySchedule): string {
  if (schedule?.cron) return schedule.cron;
  const dow = schedule?.day_of_week;
  const time = schedule?.time_utc || '08:00';
  const [hourPart] = time.split(':');
  const hour = Number.parseInt(hourPart, 10);
  if (Number.isNaN(hour) || dow == null || dow < 0 || dow > 6) {
    return DEFAULT_DELIVERY_SCHEDULE_CRON;
  }
  return `0 ${hour} * * ${dow}`;
}

/** Cron string for the schedule editor select. */
export function resolveScheduleCron(schedule?: DeliverySchedule): string {
  if (!schedule || schedule.frequency === 'none') return '';
  return legacyWeeklyToCron(schedule);
}

/**
 * Build maestro `schedule` JSON. Stores cron for the future worker; keeps weekly
 * fields when the cron is a simple weekly slot for backward compatibility.
 */
export function schedulePayloadFromCron(cron: string): DeliverySchedule {
  const trimmed = cron.trim();
  if (!trimmed) {
    return { frequency: 'none' };
  }

  const label = presetLabel(trimmed);
  const parts = trimmed.split(/\s+/);
  const base: DeliverySchedule = {
    frequency: 'cron',
    cron: trimmed,
    label,
  };

  if (
    parts.length >= 5 &&
    parts[0] === '0' &&
    parts[2] === '*' &&
    parts[3] === '*' &&
    /^\d+$/.test(parts[4])
  ) {
    const hour = Number.parseInt(parts[1], 10);
    const dow = Number.parseInt(parts[4], 10);
    if (!Number.isNaN(hour) && !Number.isNaN(dow)) {
      return {
        ...base,
        frequency: 'weekly',
        day_of_week: dow,
        time_utc: `${String(hour).padStart(2, '0')}:00`,
      };
    }
  }

  return base;
}

/** Human-readable schedule line for lists and summaries. */
export function formatDeliveryScheduleSummary(schedule?: DeliverySchedule): string {
  if (!schedule || schedule.frequency === 'none') return 'No schedule';
  if (schedule.label) return schedule.label;
  if (schedule.cron) {
    const known = presetLabel(schedule.cron);
    if (known) return known;
    return `Custom: ${schedule.cron}`;
  }
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dow = schedule.day_of_week;
  const dayLabel = dow != null && dow >= 0 && dow <= 6 ? weekdays[dow] : '—';
  const time = schedule.time_utc || '—';
  return `${dayLabel} ${time} UTC`;
}
